/**
 * Comprehensive mock data for ALL API endpoints used across ALL pages.
 * Covers: announcements, competitions, scores, medals, schedule, venues,
 * stats, media, athlete auth, athlete profile, athlete event, athlete competitions.
 */

import type {
  Announcement,
  Competition,
  ScoreEntry,
  MedalTallyEntry,
  ScheduleEntry,
  Venue,
  StatsData,
  MediaItem,
} from '@/hooks/useApi';
import type { AthleteProfile } from '@/contexts/AthleteAuthContext';
import { allPhotos, allScenes } from '@/assets/themeImages';

// ---------------------------------------------------------------------------
// Announcements
// ---------------------------------------------------------------------------

export const mockAnnouncements: Announcement[] = [
  {
    id: 'ann-1',
    title: '第14屆全國泰雅族運動會報名即將開始',
    content: '<p>各鄉區代表請注意，第14屆全國泰雅族運動會報名將於民國115年4月1日正式開始。請各鄉區儘速完成選手名單彙整。</p><p>報名截止日為4月15日，逾期將不予受理。若有任何疑問，請洽大會報名組。</p>',
    excerpt: '各鄉區代表請注意，報名將於民國115年4月1日正式開始。',
    category: '報名資訊',
    imageUrl: allScenes[0],
    author: '大會秘書處',
    createdAt: '2026-03-01',
    updatedAt: '2026-03-01',
  },
  {
    id: 'ann-2',
    title: '開幕典禮暨聖火點燃儀式流程公布',
    content: '<p>開幕典禮將於6月3日上午9時於尖石鄉運動場舉行，聖火點燃儀式為開幕最高潮。各鄉區代表團將依序進場。</p><p>請各代表團務必於8時30分前完成集合。穿著傳統泰雅族服飾進場。</p>',
    excerpt: '開幕典禮將於6月3日上午9時舉行，聖火點燃儀式為開幕最高潮。',
    category: '大會資訊',
    imageUrl: allScenes[4],
    author: '典禮組',
    createdAt: '2026-03-05',
    updatedAt: '2026-03-05',
  },
  {
    id: 'ann-3',
    title: '傳統射箭競賽規則修訂通知',
    content: '<p>為確保賽事公平，傳統射箭項目規則已依據最新全國原住民族運動會規則修訂。主要修訂包括：射距由20公尺調整為25公尺，每回合箭數由3支調整為5支。</p>',
    excerpt: '傳統射箭項目規則已修訂，射距及箭數均有調整。',
    category: '競賽資訊',
    imageUrl: allPhotos[2],
    author: '競賽組',
    createdAt: '2026-03-08',
    updatedAt: '2026-03-08',
  },
  {
    id: 'ann-4',
    title: '選手之夜活動規劃',
    content: '<p>6月4日晚間7時將於尖石鄉文化廣場舉辦選手之夜活動。活動包含傳統歌舞表演、各鄉區文化交流、營火晚會等。</p><p>歡迎所有選手及工作人員參加，現場備有傳統美食。</p>',
    excerpt: '6月4日晚間將舉辦選手之夜，包含傳統歌舞、文化交流。',
    category: '活動資訊',
    imageUrl: allScenes[9],
    author: '活動組',
    createdAt: '2026-03-09',
    updatedAt: '2026-03-09',
  },
  {
    id: 'ann-5',
    title: '交通接駁專車時刻表',
    content: '<p>大會提供高鐵新竹站往返尖石鄉接駁專車，於6月2日至6月5日期間行駛。</p><p>去程：新竹高鐵站07:00、08:00、09:00發車。回程：尖石鄉17:00、18:00、19:00發車。</p>',
    excerpt: '大會提供高鐵新竹站往返尖石鄉接駁專車服務。',
    category: '交通資訊',
    author: '交通組',
    createdAt: '2026-03-10',
    updatedAt: '2026-03-10',
  },
  {
    id: 'ann-6',
    title: '媒體採訪證申請須知',
    content: '<p>如需申請媒體採訪證，請於5月20日前提出申請。申請方式：請寄送公司名稱、記者姓名、證件照至大會信箱。</p>',
    excerpt: '媒體採訪證申請截止日為5月20日。',
    category: '媒體專區',
    imageUrl: allPhotos[3],
    author: '媒體組',
    createdAt: '2026-03-10',
    updatedAt: '2026-03-10',
  },
];

// ---------------------------------------------------------------------------
// Competitions
// ---------------------------------------------------------------------------

export const mockCompetitions: Competition[] = [
  { id: 'comp-1', name: '傳統射箭', nameEn: 'Traditional Archery', description: '以傳統泰雅族弓箭進行射箭比賽，考驗選手精準度與穩定性。', rules: '每位選手6回合，每回合5支箭，以環數計分。距離25公尺。', ageBracket: '成年組', gender: 'open', venue: '尖石鄉射箭場', registeredTeams: 9, participants: 36 },
  { id: 'comp-2', name: '負重競走', nameEn: 'Load Carrying Race', description: '背負傳統竹簍負重物進行競走，展現泰雅族祖先的生活智慧。', rules: '男子組負重30公斤、女子組20公斤，賽程2公里。', ageBracket: '成年組', gender: 'mixed', venue: '尖石鄉運動場', registeredTeams: 9, participants: 54 },
  { id: 'comp-3', name: '鋸木競賽', nameEn: 'Wood Sawing', description: '兩人一組以傳統鋸木方式比賽速度，展現默契與力量。', rules: '以傳統手鋸鋸斷直徑30公分原木，計時制。', ageBracket: '成年組', gender: 'mixed', venue: '那羅部落文化園區', registeredTeams: 9, participants: 36 },
  { id: 'comp-4', name: '拔河', nameEn: 'Tug of War', description: '各鄉區代表隊進行拔河比賽，考驗團隊精神與協調力。', rules: '每隊8人（含2名女性），三戰兩勝。', ageBracket: '成年組', gender: 'mixed', venue: '尖石鄉運動場', registeredTeams: 9, participants: 72 },
  { id: 'comp-5', name: '傳統編織', nameEn: 'Traditional Weaving', description: '以泰雅族傳統織布技法進行限時編織創作比賽。', rules: '限時90分鐘，以作品完整度、技法、創意評分。', ageBracket: '不限', gender: 'female', venue: '泰雅文物館', registeredTeams: 9, participants: 27 },
  { id: 'comp-6', name: '傳統歌謠吟唱', nameEn: 'Traditional Song', description: '演唱泰雅族傳統歌謠，保存與傳承文化之美。', rules: '每隊3-5人，演唱1-2首傳統歌謠，限時10分鐘。', ageBracket: '不限', gender: 'open', venue: '尖石鄉文化廣場', registeredTeams: 9, participants: 45 },
  { id: 'comp-7', name: '傳統田徑100公尺', nameEn: 'Traditional 100m Sprint', description: '穿著傳統服飾的100公尺短跑比賽。', rules: '分預賽、複賽、決賽三輪。穿著傳統服飾。', ageBracket: '青年組', gender: 'mixed', venue: '尖石鄉運動場', registeredTeams: 9, participants: 36 },
  { id: 'comp-8', name: '口簧琴演奏', nameEn: 'Jaw Harp', description: '泰雅族傳統口簧琴演奏比賽，展現音樂天賦。', rules: '個人演奏3分鐘，以技巧、音色、表現力評分。', ageBracket: '不限', gender: 'open', venue: '泰雅文物館', registeredTeams: 7, participants: 14 },
  { id: 'comp-9', name: '狩獵陷阱製作', nameEn: 'Hunting Trap Making', description: '展示泰雅族傳統狩獵陷阱製作技術。', rules: '限時60分鐘，製作指定類型陷阱，以實用性、技法評分。', ageBracket: '成年組', gender: 'male', venue: '那羅部落文化園區', registeredTeams: 9, participants: 27 },
  { id: 'comp-10', name: '傳統舞蹈', nameEn: 'Traditional Dance', description: '各鄉區代表隊表演泰雅族傳統舞蹈。', rules: '每隊10-20人，表演時間8-12分鐘，以整齊度、傳統性、創意評分。', ageBracket: '不限', gender: 'open', venue: '尖石鄉文化廣場', registeredTeams: 9, participants: 135 },
];

// ---------------------------------------------------------------------------
// Live Scores
// ---------------------------------------------------------------------------

export const mockLiveScores: ScoreEntry[] = [
  { id: 's1', competitionId: 'comp-1', competitionName: '傳統射箭', rank: 1, athleteName: '瓦旦．達路', township: '尖石鄉', result: '285環', status: 'completed' },
  { id: 's2', competitionId: 'comp-1', competitionName: '傳統射箭', rank: 2, athleteName: '尤幹．巴萬', township: '復興區', result: '278環', status: 'completed' },
  { id: 's3', competitionId: 'comp-1', competitionName: '傳統射箭', rank: 3, athleteName: '達陸．哈勇', township: '大同鄉', result: '271環', status: 'completed' },
  { id: 's4', competitionId: 'comp-1', competitionName: '傳統射箭', rank: 4, athleteName: '比令．亞布', township: '南澳鄉', result: '265環', status: 'completed' },
  { id: 's5', competitionId: 'comp-2', competitionName: '負重競走', rank: 1, athleteName: '哈勇．拉拜', township: '五峰鄉', result: '12:34', status: 'live' },
  { id: 's6', competitionId: 'comp-2', competitionName: '負重競走', rank: 2, athleteName: '雅各．依將', township: '秀林鄉', result: '13:02', status: 'live' },
  { id: 's7', competitionId: 'comp-2', competitionName: '負重競走', rank: 3, athleteName: '馬耀．武棟', township: '萬榮鄉', result: '13:28', status: 'live' },
  { id: 's8', competitionId: 'comp-3', competitionName: '鋸木競賽', rank: 0, athleteName: '-', township: '-', result: '-', status: 'upcoming' },
  { id: 's9', competitionId: 'comp-4', competitionName: '拔河', rank: 1, athleteName: '尖石鄉代表隊', township: '尖石鄉', result: '3:0', status: 'live' },
  { id: 's10', competitionId: 'comp-4', competitionName: '拔河', rank: 2, athleteName: '復興區代表隊', township: '復興區', result: '2:1', status: 'live' },
  { id: 's11', competitionId: 'comp-5', competitionName: '傳統編織', rank: 0, athleteName: '-', township: '-', result: '-', status: 'notStarted' },
  { id: 's12', competitionId: 'comp-7', competitionName: '傳統田徑100公尺', rank: 1, athleteName: '拉娃．尤道', township: '卓溪鄉', result: '11.52秒', status: 'completed' },
  { id: 's13', competitionId: 'comp-7', competitionName: '傳統田徑100公尺', rank: 2, athleteName: '巴奈．達力', township: '和平區', result: '11.78秒', status: 'completed' },
  { id: 's14', competitionId: 'comp-7', competitionName: '傳統田徑100公尺', rank: 3, athleteName: '達給．蘇路', township: '尖石鄉', result: '11.95秒', status: 'completed' },
];

// ---------------------------------------------------------------------------
// Medal Tally
// ---------------------------------------------------------------------------

export const mockMedalTally: MedalTallyEntry[] = [
  { township: '尖石鄉', gold: 4, silver: 2, bronze: 3, total: 9 },
  { township: '復興區', gold: 3, silver: 3, bronze: 1, total: 7 },
  { township: '五峰鄉', gold: 2, silver: 2, bronze: 2, total: 6 },
  { township: '大同鄉', gold: 2, silver: 1, bronze: 3, total: 6 },
  { township: '南澳鄉', gold: 1, silver: 3, bronze: 2, total: 6 },
  { township: '秀林鄉', gold: 1, silver: 2, bronze: 1, total: 4 },
  { township: '萬榮鄉', gold: 1, silver: 1, bronze: 2, total: 4 },
  { township: '卓溪鄉', gold: 1, silver: 1, bronze: 1, total: 3 },
  { township: '和平區', gold: 0, silver: 1, bronze: 1, total: 2 },
];

// ---------------------------------------------------------------------------
// Schedule
// ---------------------------------------------------------------------------

export const mockSchedule: ScheduleEntry[] = [
  // Day 1 - June 3
  { id: 'sch-1', day: 1, startTime: '08:00', endTime: '08:30', title: '各代表團報到', titleEn: 'Team Check-in', venue: '尖石鄉運動場', category: 'ceremony' },
  { id: 'sch-2', day: 1, startTime: '09:00', endTime: '10:30', title: '開幕典禮暨聖火點燃', titleEn: 'Opening Ceremony & Sacred Flame', venue: '尖石鄉運動場', category: 'opening' },
  { id: 'sch-3', day: 1, startTime: '10:30', endTime: '12:00', title: '傳統射箭預賽', titleEn: 'Traditional Archery Preliminaries', venue: '尖石鄉射箭場', category: 'competition' },
  { id: 'sch-4', day: 1, startTime: '10:30', endTime: '12:00', title: '傳統田徑100公尺預賽', titleEn: '100m Sprint Preliminaries', venue: '尖石鄉運動場', category: 'competition' },
  { id: 'sch-5', day: 1, startTime: '13:30', endTime: '15:30', title: '拔河初賽', titleEn: 'Tug of War Round 1', venue: '尖石鄉運動場', category: 'competition' },
  { id: 'sch-6', day: 1, startTime: '13:30', endTime: '15:00', title: '傳統編織競賽', titleEn: 'Traditional Weaving', venue: '泰雅文物館', category: 'competition' },
  { id: 'sch-7', day: 1, startTime: '15:30', endTime: '17:00', title: '負重競走預賽', titleEn: 'Load Carrying Preliminaries', venue: '尖石鄉運動場', category: 'competition' },
  // Day 2 - June 4
  { id: 'sch-8', day: 2, startTime: '08:30', endTime: '10:30', title: '傳統射箭決賽', titleEn: 'Traditional Archery Finals', venue: '尖石鄉射箭場', category: 'competition' },
  { id: 'sch-9', day: 2, startTime: '08:30', endTime: '10:30', title: '鋸木競賽', titleEn: 'Wood Sawing', venue: '那羅部落文化園區', category: 'competition' },
  { id: 'sch-10', day: 2, startTime: '10:30', endTime: '12:00', title: '負重競走決賽', titleEn: 'Load Carrying Finals', venue: '尖石鄉運動場', category: 'competition' },
  { id: 'sch-11', day: 2, startTime: '13:30', endTime: '15:00', title: '拔河決賽', titleEn: 'Tug of War Finals', venue: '尖石鄉運動場', category: 'competition' },
  { id: 'sch-12', day: 2, startTime: '13:30', endTime: '15:00', title: '狩獵陷阱製作', titleEn: 'Hunting Trap Making', venue: '那羅部落文化園區', category: 'competition' },
  { id: 'sch-13', day: 2, startTime: '15:00', endTime: '17:00', title: '傳統歌謠吟唱', titleEn: 'Traditional Song', venue: '尖石鄉文化廣場', category: 'competition' },
  { id: 'sch-14', day: 2, startTime: '15:00', endTime: '16:30', title: '口簧琴演奏', titleEn: 'Jaw Harp Performance', venue: '泰雅文物館', category: 'competition' },
  { id: 'sch-15', day: 2, startTime: '19:00', endTime: '21:30', title: '選手之夜', titleEn: "Athletes' Night", venue: '尖石鄉文化廣場', category: 'athletesNight' },
  // Day 3 - June 5
  { id: 'sch-16', day: 3, startTime: '08:30', endTime: '10:00', title: '傳統田徑100公尺決賽', titleEn: '100m Sprint Finals', venue: '尖石鄉運動場', category: 'competition' },
  { id: 'sch-17', day: 3, startTime: '10:00', endTime: '12:00', title: '傳統舞蹈表演賽', titleEn: 'Traditional Dance', venue: '尖石鄉文化廣場', category: 'competition' },
  { id: 'sch-18', day: 3, startTime: '14:00', endTime: '15:00', title: '頒獎典禮', titleEn: 'Award Ceremony', venue: '尖石鄉運動場', category: 'ceremony' },
  { id: 'sch-19', day: 3, startTime: '15:00', endTime: '16:30', title: '閉幕典禮', titleEn: 'Closing Ceremony', venue: '尖石鄉運動場', category: 'closing' },
];

// ---------------------------------------------------------------------------
// Venues
// ---------------------------------------------------------------------------

export const mockVenues: Venue[] = [
  {
    id: 'v-1', name: '尖石鄉運動場', nameEn: 'Jianshi Township Sports Ground',
    description: '主場地，用於開閉幕典禮及田徑、拔河、負重競走等項目。設有觀眾席、遮陽棚及無障礙設施。',
    address: '新竹縣尖石鄉嘉樂村2鄰36號', capacity: 3000,
    imageUrl: allScenes[1],
    mapUrl: 'https://maps.google.com/?q=24.7054,121.2007',
    competitions: ['負重競走', '拔河', '傳統田徑100公尺'],
    facilities: ['觀眾席', '無障礙設施', '停車場', '飲水機', '廁所'],
  },
  {
    id: 'v-2', name: '尖石鄉射箭場', nameEn: 'Jianshi Archery Range',
    description: '專業射箭場地，設有標準靶位、安全網及觀賽區。場地周圍環繞山林，充滿泰雅族傳統狩獵氛圍。',
    address: '新竹縣尖石鄉嘉樂村射箭場', capacity: 500,
    imageUrl: allPhotos[2],
    mapUrl: 'https://maps.google.com/?q=24.7064,121.2017',
    competitions: ['傳統射箭'],
    facilities: ['安全網', '觀賽區', '休息區', '飲水機'],
  },
  {
    id: 'v-3', name: '泰雅文物館', nameEn: 'Atayal Museum',
    description: '展示泰雅族文化與歷史的文物館，本次用於傳統編織及口簧琴比賽場地。場館內有空調，適合精細手工藝展示。',
    address: '新竹縣尖石鄉嘉樂村2鄰40號', capacity: 200,
    imageUrl: allScenes[5],
    mapUrl: 'https://maps.google.com/?q=24.7044,121.2027',
    competitions: ['傳統編織', '口簧琴演奏'],
    facilities: ['空調', 'WiFi', '無障礙設施', '停車場'],
  },
  {
    id: 'v-4', name: '那羅部落文化園區', nameEn: 'Naro Tribal Cultural Park',
    description: '位於那羅溪畔的文化園區，擁有天然林地，適合進行鋸木及狩獵陷阱等傳統技藝比賽。',
    address: '新竹縣尖石鄉錦屏村那羅', capacity: 800,
    imageUrl: allScenes[6],
    mapUrl: 'https://maps.google.com/?q=24.6854,121.2107',
    competitions: ['鋸木競賽', '狩獵陷阱製作'],
    facilities: ['停車場', '步道', '廁所', '飲水機'],
  },
  {
    id: 'v-5', name: '尖石鄉文化廣場', nameEn: 'Jianshi Cultural Plaza',
    description: '開放式表演廣場，設有舞台、音響設備及觀眾席。用於傳統歌謠、舞蹈及選手之夜活動。',
    address: '新竹縣尖石鄉嘉樂村文化路1號', capacity: 1500,
    imageUrl: allScenes[8],
    mapUrl: 'https://maps.google.com/?q=24.7034,121.2037',
    competitions: ['傳統歌謠吟唱', '傳統舞蹈'],
    facilities: ['舞台', '音響', '觀眾席', '停車場', '廁所', '飲水機'],
  },
];

// ---------------------------------------------------------------------------
// Stats
// ---------------------------------------------------------------------------

export const mockStats: StatsData = {
  totalParticipants: 482,
  totalTownships: 9,
  totalCompetitions: 10,
  totalMedals: 47,
  participationByTownship: [
    { name: '尖石鄉', count: 68 },
    { name: '五峰鄉', count: 54 },
    { name: '復興區', count: 62 },
    { name: '大同鄉', count: 52 },
    { name: '南澳鄉', count: 58 },
    { name: '秀林鄉', count: 49 },
    { name: '萬榮鄉', count: 48 },
    { name: '卓溪鄉', count: 46 },
    { name: '和平區', count: 45 },
  ],
  participationByCompetition: [
    { name: '傳統舞蹈', count: 135 },
    { name: '拔河', count: 72 },
    { name: '負重競走', count: 54 },
    { name: '傳統歌謠吟唱', count: 45 },
    { name: '傳統射箭', count: 36 },
    { name: '傳統田徑100公尺', count: 36 },
    { name: '鋸木競賽', count: 36 },
    { name: '傳統編織', count: 27 },
    { name: '狩獵陷阱製作', count: 27 },
    { name: '口簧琴演奏', count: 14 },
  ],
  medalDistribution: [
    { township: '尖石鄉', gold: 4, silver: 2, bronze: 3 },
    { township: '復興區', gold: 3, silver: 3, bronze: 1 },
    { township: '五峰鄉', gold: 2, silver: 2, bronze: 2 },
    { township: '大同鄉', gold: 2, silver: 1, bronze: 3 },
    { township: '南澳鄉', gold: 1, silver: 3, bronze: 2 },
    { township: '秀林鄉', gold: 1, silver: 2, bronze: 1 },
    { township: '萬榮鄉', gold: 1, silver: 1, bronze: 2 },
    { township: '卓溪鄉', gold: 1, silver: 1, bronze: 1 },
    { township: '和平區', gold: 0, silver: 1, bronze: 1 },
  ],
  registrationTrend: [
    { date: '2026-04-01', count: 45 },
    { date: '2026-04-03', count: 112 },
    { date: '2026-04-05', count: 198 },
    { date: '2026-04-07', count: 267 },
    { date: '2026-04-09', count: 335 },
    { date: '2026-04-11', count: 401 },
    { date: '2026-04-13', count: 452 },
    { date: '2026-04-15', count: 482 },
  ],
};

// ---------------------------------------------------------------------------
// Media
// ---------------------------------------------------------------------------

export const mockMedia: MediaItem[] = [
  { id: 'm-1', type: 'photo', title: '開幕典禮精彩瞬間', url: allPhotos[0], thumbnailUrl: allPhotos[0], createdAt: '2026-06-03' },
  { id: 'm-2', type: 'photo', title: '傳統射箭比賽實況', url: allPhotos[2], thumbnailUrl: allPhotos[2], createdAt: '2026-06-03' },
  { id: 'm-3', type: 'photo', title: '各鄉區代表進場', url: allPhotos[1], thumbnailUrl: allPhotos[1], createdAt: '2026-06-03' },
  { id: 'm-4', type: 'photo', title: '文化交流活動', url: allPhotos[3], thumbnailUrl: allPhotos[3], createdAt: '2026-06-04' },
  { id: 'm-5', type: 'photo', title: '泰雅傳統服飾展示', url: allPhotos[4], thumbnailUrl: allPhotos[4], createdAt: '2026-06-04' },
  { id: 'm-6', type: 'photo', title: '大會活動主視覺', url: allPhotos[5], thumbnailUrl: allPhotos[5], createdAt: '2026-03-01' },
  { id: 'm-7', type: 'photo', title: '賽事宣傳橫幅', url: allPhotos[6], thumbnailUrl: allPhotos[6], createdAt: '2026-03-01' },
  { id: 'm-8', type: 'photo', title: '射箭場地全景', url: allScenes[0], thumbnailUrl: allScenes[0], createdAt: '2026-06-03' },
  { id: 'm-9', type: 'photo', title: '拔河精彩對決', url: allScenes[9], thumbnailUrl: allScenes[9], createdAt: '2026-06-04' },
  { id: 'm-10', type: 'photo', title: '負重競走實況', url: allScenes[8], thumbnailUrl: allScenes[8], createdAt: '2026-06-04' },
  { id: 'm-11', type: 'photo', title: '山林中的射箭場', url: allScenes[5], thumbnailUrl: allScenes[5], createdAt: '2026-06-03' },
  { id: 'm-12', type: 'photo', title: '傳統歌謠表演', url: allScenes[7], thumbnailUrl: allScenes[7], createdAt: '2026-06-04' },
  { id: 'm-13', type: 'video', title: '第14屆全國泰雅族運動會宣傳影片', url: '', thumbnailUrl: allScenes[4], createdAt: '2026-02-15' },
  { id: 'm-14', type: 'video', title: '傳統射箭決賽精華', url: '', thumbnailUrl: allPhotos[2], createdAt: '2026-06-04' },
];

// ---------------------------------------------------------------------------
// Athlete Auth & Profile
// ---------------------------------------------------------------------------

export const mockAthleteProfile: AthleteProfile = {
  id: 'ath-001',
  name: '瓦旦．達路',
  nameEn: 'Watan Dalu',
  gender: '男',
  dateOfBirth: '1995-06-15',
  ethnicity: '泰雅族',
  nationalId: 'A1234*****',
  phone: '0912-345-678',
  email: 'watan@example.com',
  address: '新竹縣尖石鄉嘉樂村2鄰36號',
  emergencyContact: '達路．哈勇',
  emergencyPhone: '0923-456-789',
  photoUrl: '/images/athletes/athlete-archer.png',
  township: '尖石鄉',
  eventId: 'event-14',
  eventName: '第14屆全國泰雅族運動會',
  registrationStatus: 'APPROVED',
};

// ---------------------------------------------------------------------------
// Athlete Event
// ---------------------------------------------------------------------------

export const mockAthleteEvent = {
  id: 'event-14',
  name: '第14屆全國泰雅族運動會暨第3屆新竹縣原住民族傳統技藝競賽',
  nameEn: '14th National Atayal Games & 3rd Hsinchu County Indigenous Traditional Skills Competition',
  startDate: '2026-06-03',
  endDate: '2026-06-05',
  location: '新竹縣尖石鄉',
  locationEn: 'Jianshi Township, Hsinchu County',
  address: '新竹縣尖石鄉嘉樂村2鄰36號',
  mapUrl: 'https://maps.google.com/?q=24.7054,121.2007',
  description: '第14屆全國泰雅族運動會將於民國115年（2026年）6月3日至5日，在新竹縣尖石鄉盛大舉辦。本屆運動會由尖石鄉公所主辦，結合傳統競技與文化傳承，邀請全國9個泰雅族鄉區共同參與。',
  rules: '一、參賽資格：泰雅族、賽德克族、太魯閣族、賽夏族及其他（含說明）。二、報名方式：由各鄉區統一報名。三、服裝：開閉幕式需穿著傳統服飾。',
  schedule: mockSchedule.map((s) => ({
    ...s,
    date: s.day === 1 ? '2026-06-03' : s.day === 2 ? '2026-06-04' : '2026-06-05',
    isMyCompetition: s.title.includes('射箭'),
  })),
};

// ---------------------------------------------------------------------------
// Athlete Competitions
// ---------------------------------------------------------------------------

export const mockAthleteCompetitions = [
  {
    id: 'comp-1',
    name: '傳統射箭',
    nameEn: 'Traditional Archery',
    date: '2026-06-03',
    time: '10:30',
    endTime: '12:00',
    venue: '尖石鄉射箭場',
    venueEn: 'Jianshi Archery Range',
    status: 'completed' as const,
    result: '285環',
    rank: 1,
  },
  {
    id: 'comp-1-final',
    name: '傳統射箭決賽',
    nameEn: 'Traditional Archery Finals',
    date: '2026-06-04',
    time: '08:30',
    endTime: '10:30',
    venue: '尖石鄉射箭場',
    venueEn: 'Jianshi Archery Range',
    status: 'completed' as const,
    result: '298環',
    rank: 1,
  },
];
